import React from 'react';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon, InformationCircleIcon, ExclamationCircleIcon } from '@heroicons/react/20/solid';

/**
 * Alert Atom - component for displaying informational messages with different styles
 * 
 * @param {string} variant - 'neutral', 'info', 'warning', 'error', 'success'
 * @param {string} message - The message to display
 * @param {string} actionText - Optional text for the action button
 * @param {string} actionHref - Optional href for the action link
 * @param {function} onAction - Optional action handler (alternative to actionHref)
 * @param {string} className - Additional CSS classes
 */
const Alert = ({ 
  variant = 'neutral', 
  message, 
  actionText, 
  actionHref, 
  onAction,
  className = '',
}) => {
  // Define styles for each variant
  const variantStyles = {
    neutral: {
      container: 'bg-gray-50',
      icon: 'text-gray-400',
      text: 'text-gray-700',
      action: 'text-gray-600 hover:text-gray-800 bg-gray-50 hover:bg-gray-100',
      Icon: InformationCircleIcon
    },
    info: {
      container: 'bg-blue-50',
      icon: 'text-blue-400',
      text: 'text-blue-700',
      action: 'text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100',
      Icon: InformationCircleIcon
    },
    warning: {
      container: 'bg-yellow-50',
      icon: 'text-yellow-400',
      text: 'text-yellow-700',
      action: 'text-yellow-600 hover:text-yellow-800 bg-yellow-50 hover:bg-yellow-100',
      Icon: ExclamationTriangleIcon
    },
    error: {
      container: 'bg-red-50',
      icon: 'text-red-400',
      text: 'text-red-700',
      action: 'text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100',
      Icon: ExclamationCircleIcon
    }
  };

  const style = variantStyles[variant] || variantStyles.neutral;
  const Icon = style.Icon;

  // Render action button or link based on props
  const renderAction = () => {
    if (!actionText) return null;

    if (actionHref) {
      return (
        <div className="mt-3">
          <Link
            to={actionHref}
            className={`rounded-md px-3 py-2 text-sm font-semibold ${style.action}`}
          >
            {actionText}
          </Link>
        </div>
      );
    }

    if (onAction) {
      return (
        <div className="mt-3">
          <button
            type="button"
            onClick={onAction}
            className={`rounded-md px-3 py-2 text-sm font-semibold ${style.action}`}
          >
            {actionText}
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={`mt-6 rounded-md ${style.container} p-4 ${className}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          <Icon className={`h-5 w-5 ${style.icon}`} aria-hidden="true" />
        </div>
        <div className="ml-3 flex-1 md:flex md:justify-between">
          <p className={`text-sm ${style.text}`}>{message}</p>
        </div>
      </div>
      {renderAction()}
    </div>
  );
};

export default Alert; 